import { useEffect, useState } from "react";
import { api, fmtDate, fmtTime, type Admin } from "../api";
import { ActionMenu } from "../components/ActionMenu";

interface Props {
  meId: string;
}

export function AdminsTab({ meId }: Props) {
  const [items, setItems] = useState<Admin[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [pwTarget, setPwTarget] = useState<Admin | null>(null);
  const [phoneTarget, setPhoneTarget] = useState<Admin | null>(null);

  async function reload() {
    try {
      const r = await api.listAdmins();
      setItems(r.items);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "load_failed");
    }
  }
  useEffect(() => {
    reload();
  }, []);

  async function remove(a: Admin) {
    if (a.id === meId) {
      alert("You cannot delete yourself.");
      return;
    }
    if (!confirm(`Delete admin ${a.email}?`)) return;
    try {
      await api.deleteAdmin(a.id);
      reload();
    } catch (e) {
      alert(e instanceof Error ? e.message : "delete_failed");
    }
  }

  return (
    <>
      <div className="panel">
        <div className="panel-head">
          <h2>
            Admins
            {items && <span className="count">{items.length}</span>}
          </h2>
          <div className="spacer" />
          <button onClick={() => setShowCreate(true)}>New admin</button>
        </div>
        {err && <div className="error">{err}</div>}
        {items === null ? (
          <div className="empty">Loading…</div>
        ) : (
          <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Admin</th>
                <th>Phone</th>
                <th>Created</th>
                <th>Last login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id}>
                  <td className="primary" data-label="Admin">
                    <div className="main">
                      {a.email}
                      {a.id === meId && <span className="muted"> (you)</span>}
                    </div>
                  </td>
                  <td data-label="Phone" className="nowrap">
                    {a.phone ? <span className="mono">{a.phone}</span> : <span className="muted">—</span>}
                  </td>
                  <td data-label="Created" className="nowrap" title={fmtTime(a.created_at)}>
                    {fmtDate(a.created_at)}
                  </td>
                  <td data-label="Last login" className="nowrap">{fmtTime(a.last_login_at)}</td>
                  <td className="actions" data-label="Actions">
                    <div className="action-group">
                      <button className="secondary sm" onClick={() => setPhoneTarget(a)}>Phone</button>
                      <ActionMenu
                        title={a.email}
                        items={[
                          { label: "Set phone", onClick: () => setPhoneTarget(a) },
                          { label: "Set password", onClick: () => setPwTarget(a) },
                          { label: "Delete", onClick: () => remove(a), danger: true, disabled: a.id === meId, sep: true },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateAdminModal
          onCancel={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            reload();
          }}
        />
      )}
      {pwTarget && (
        <SetPasswordModal
          target={pwTarget}
          onCancel={() => setPwTarget(null)}
          onDone={() => setPwTarget(null)}
        />
      )}
      {phoneTarget && (
        <SetPhoneModal
          target={phoneTarget}
          onCancel={() => setPhoneTarget(null)}
          onDone={() => {
            setPhoneTarget(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function CreateAdminModal({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.createAdmin({ email, password, phone: phone.trim() || null });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>New admin</h3>
        <label>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <label>Password (min 12 chars)</label>
        <input
          type="password"
          minLength={12}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <label>Phone (optional, used for SMS login)</label>
        <input
          type="tel"
          placeholder="+90 5xx xxx xx xx"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        {err && <div className="error">{err}</div>}
        <div className="modal-foot">
          <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" disabled={busy}>{busy ? "Creating…" : "Create"}</button>
        </div>
      </form>
    </div>
  );
}

function SetPasswordModal({
  target,
  onCancel,
  onDone,
}: {
  target: Admin;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.setAdminPassword(target.id, password);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "update_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Set password for {target.email}</h3>
        <p className="muted">All other sessions for this admin will be invalidated.</p>
        <label>New password (min 12 chars)</label>
        <input
          type="password"
          minLength={12}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {err && <div className="error">{err}</div>}
        <div className="modal-foot">
          <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function SetPhoneModal({
  target,
  onCancel,
  onDone,
}: {
  target: Admin;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [phone, setPhone] = useState(target.phone || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.setAdminPhone(target.id, phone.trim() || null);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "update_failed");
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    setErr(null);
    try {
      await api.setAdminPhone(target.id, null);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "update_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Phone for {target.email}</h3>
        <p className="muted">
          The admin signs in by sending <code>ADMN &lt;OTP&gt;</code> from this number to the
          operator's inbound SMS number.
        </p>
        <label>Phone</label>
        <input
          type="tel"
          placeholder="+90 5xx xxx xx xx"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        {err && <div className="error">{err}</div>}
        <div className="modal-foot">
          {target.phone && (
            <button type="button" className="danger" onClick={clear} disabled={busy}>
              Clear
            </button>
          )}
          <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
